import { useEffect, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { wait } from '../../../helpers/commonHelpers';
import { UserType } from '../../../constants/profileConstants';
import {
  ExistingInvestorProfileFlags,
  GuestVisitInvestor,
} from '../../investors/models/guestInvestorModel';
import {
  activateGuestInvestorEndpoint,
  getGuestVisitInvestorDetailEndpoint,
  updateGuestVisitEndpoint,
} from '../../investors/services/guestInvestorService';
import {
  assignProperPreSaleVisitEndpoint,
  getAvailableTimeProjectVisitsEndpoint,
} from '../../investors/services/visitsInvestorService';
import {
  getLocationKey,
  getVisitAddress,
} from '../../investors/helpers/visitAddressHelper';
import useOfficeAddress from '../../investors/hooks/useOfficeAddress';
import { timeToMinutes, toInputDate } from '../components/VisitDetailParts';

// Mismo flujo de "Aceptar asesoría" del detalle de asesoría de invitado
// (InvestGuestVisitDetail): tipo de visita (virtual/presencial), lugar y
// proper; si el cliente aún es guest se activa como inversionista antes de
// asignar el proper. Sólo carga datos cuando `enabled` (visita pendiente).
export interface VisitTimeSlot {
  minutes: number;
  label: string;
  isBlocked: boolean;
}

const useAcceptGuestVisit = (visitId: string, enabled: boolean) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { officeAddress } = useOfficeAddress();

  const [guestVisit, setGuestVisit] = useState<GuestVisitInvestor | null>(null);
  const [visitType, setVisitType] = useState<string>('virtual');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [properId, setProperId] = useState('');
  // Cambio opcional de fecha/hora: sólo se envía si se elige fecha y hora.
  const [original, setOriginal] = useState<{
    date: string;
    minutes?: number;
    label: string;
  }>({ date: '', label: '' });
  const [newDate, setNewDateState] = useState('');
  const [newTime, setNewTime] = useState('');
  const [timeSlots, setTimeSlots] = useState<VisitTimeSlot[]>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!enabled || !visitId) return;
    callEndpoint(getGuestVisitInvestorDetailEndpoint(visitId, token))
      .then(({ status, data }) => {
        const response = fetchResponseAdapter(data);
        if (status !== 200) {
          setError(response.message);
          return;
        }
        const detail: GuestVisitInvestor = response.data;
        setGuestVisit(detail);
        // Precarga la fecha y hora con las que ya está agendada la asesoría.
        const orig = {
          date: toInputDate(detail.visitInfo.visit_date),
          minutes:
            detail.visitInfo.visit_time_minutes ??
            timeToMinutes(detail.visitInfo.visit_time),
          label: detail.visitInfo.visit_time,
        };
        setOriginal(orig);
        setNewDateState(orig.date);
        setNewTime(orig.minutes !== undefined ? String(orig.minutes) : '');
        fetchSlots(detail.projectInfo.id, orig.date, orig);
        setVisitType(detail.visitInfo.visit_type || 'virtual');
        setProperId(
          detail.visitInfo.visit_proper_selected_id ??
            detail.visitInfo.proper_initial_selected_id ??
            '',
        );
      })
      .catch((e) => setError(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, visitId]);

  const fetchSlots = async (
    projectId: string,
    date: string,
    orig: { date: string; minutes?: number; label: string },
  ) => {
    setTimeSlots([]);
    if (!date) return;
    const { status, data } = await callEndpoint(
      getAvailableTimeProjectVisitsEndpoint(projectId, date, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200 || !Array.isArray(response.data)) {
      setError(response.message);
      return;
    }
    const slots: VisitTimeSlot[] = response.data.map((slot: any) => ({
      minutes: slot.minutes,
      label: slot.time_value ?? slot.time,
      // El horario de esta misma visita aparece ocupado: se deja elegible.
      isBlocked:
        !!slot.is_blocked &&
        !(date === orig.date && slot.minutes === orig.minutes),
    }));
    if (
      date === orig.date &&
      orig.minutes !== undefined &&
      !slots.some((slot) => slot.minutes === orig.minutes)
    ) {
      slots.unshift({
        minutes: orig.minutes,
        label: orig.label,
        isBlocked: false,
      });
    }
    setTimeSlots(slots);
  };

  const setNewDate = async (date: string) => {
    setNewDateState(date);
    setNewTime('');
    if (guestVisit) await fetchSlots(guestVisit.projectInfo.id, date, original);
  };

  const locationKey =
    selectedLocation ??
    (guestVisit
      ? getLocationKey(
          guestVisit.visitInfo.location,
          guestVisit.projectInfo.address,
        )
      : 'office');

  const isGuest = guestVisit?.userInfo.type === UserType.GUEST;

  // true si la visita quedó aceptada.
  const accept = async (): Promise<boolean> => {
    if (!guestVisit || !properId) return false;
    if (newDate && newTime === '') {
      setError('Selecciona la hora de la asesoría.');
      return false;
    }
    const scheduleChanged =
      !!newDate &&
      newTime !== '' &&
      (newDate !== original.date || Number(newTime) !== original.minutes);
    setError('');
    setIsSubmitting(true);
    try {
      if (scheduleChanged) {
        const { status, data } = await callEndpoint(
          updateGuestVisitEndpoint(
            guestVisit.visitInfo.id,
            { date: newDate, start_time: Number(newTime) },
            token,
          ),
        );
        if (status !== 200) {
          setError(fetchResponseAdapter(data).message);
          return false;
        }
      }
      if (isGuest) {
        const { status, data } = await callEndpoint(
          activateGuestInvestorEndpoint(guestVisit.userInfo.id, token),
        );
        if (status !== 200) {
          const response = fetchResponseAdapter(data);
          const flags: ExistingInvestorProfileFlags = response.data;
          const notes = [
            flags?.existsProfile && '(Correo existente en otros perfiles)',
            flags?.existsProfileByPhone &&
              '(Número existente en otros perfiles) Verifica con administrador ya que no pueden haber teléfonos repetidos en un mismo tipo de usuarios',
          ].filter(Boolean);
          setError([response.message, ...notes].join(' '));
          return false;
        }
        await wait(2000);
      }
      const { status, data } = await callEndpoint(
        assignProperPreSaleVisitEndpoint(
          visitId,
          {
            proper_id: properId,
            visit_type: visitType,
            location: getVisitAddress(
              visitType,
              locationKey,
              officeAddress,
              guestVisit.projectInfo.address,
            ),
          },
          token,
        ),
      );
      if (status !== 200) {
        setError(fetchResponseAdapter(data).message);
        return false;
      }
      return true;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    guestVisit,
    isGuest,
    visitType,
    setVisitType,
    locationKey,
    setSelectedLocation,
    properId,
    setProperId,
    officeAddress,
    newDate,
    setNewDate,
    newTime,
    setNewTime,
    timeSlots,
    error,
    isSubmitting,
    accept,
  };
};

export default useAcceptGuestVisit;
